import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearch } from 'wouter'
import type { DriveStep } from 'driver.js'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import '../styles/driver-overrides.css'
import { api } from '@/lib/api'
import { useUser } from '@/contexts/UserContext'
import { SHARED_DRIVER_CONFIG } from '@/lib/guide-driver-config'

interface UseTourOptions {
  /** Si false, deshabilita el auto-inicio (útil para relanzar manualmente). Default: true */
  autoStart?: boolean
}

interface UseTourResult {
  /** Inicia el tour manualmente, independientemente del estado seen. */
  startTour: () => void
}

/**
 * Hook genérico que envuelve driver.js para guiar al usuario a través de
 * elementos del DOM marcados con atributos data-tour.
 *
 * - Auto-inicia una sola vez si el usuario aún no ha visto el tour.
 * - Al cerrar (X o paso final), llama POST /me/tours/{tourId}/visto y
 *   refresca el usuario desde la API.
 * - Un fallo del POST nunca rompe el componente que consume el hook.
 * - Suprime el auto-inicio si la URL contiene ?wizard=completado.
 *
 * IMPORTANTE: `steps` debe ser una referencia ESTABLE (constante de módulo o
 * memoizada). El driver se memoiza solo por `tourId`, así que un array inline
 * que cambie en cada render NO se reflejará y dejaría una closure obsoleta.
 */
export function useTour(
  tourId: string,
  steps: DriveStep[],
  options?: UseTourOptions,
): UseTourResult {
  const { user, isLoading, refreshUser } = useUser()
  const search = useSearch()

  const seen = Boolean(user?.tours_vistos?.[tourId])
  const autoStart = options?.autoStart !== false

  // Ref de guardia: el callback onDestroyed solo ejecuta la llamada una vez
  // por sesión de driver para evitar POST duplicados ante renders concurrentes.
  const markedRef = useRef(false)

  // Ref one-shot: el auto-inicio solo se dispara una vez por mount del componente.
  const autoStartFiredRef = useRef(false)

  const driverObj = useMemo(
    () =>
      driver({
        ...SHARED_DRIVER_CONFIG,
        steps,
        onDestroyed: () => {
          if (markedRef.current) return
          markedRef.current = true
          // POST y refreshUser en fire-and-forget: un fallo no debe crashear el dashboard.
          ;(async () => {
            try {
              await api.post(`/me/tours/${tourId}/visto`)
              await refreshUser()
            } catch {
              // Fallo silencioso: el tour podrá re-marcar en la próxima sesión.
            }
          })()
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tourId],
  )

  useEffect(() => {
    // Cada vez que el tourId cambia, reiniciar la guardia de marcado.
    markedRef.current = false
    autoStartFiredRef.current = false
  }, [tourId])

  useEffect(() => {
    if (!autoStart) return
    if (isLoading) return
    if (seen) return
    if (autoStartFiredRef.current) return

    // Suprimir si el wizard de onboarding acaba de completarse.
    const params = new URLSearchParams(search)
    if (params.get('wizard') === 'completado') return

    autoStartFiredRef.current = true
    const timer = setTimeout(() => {
      driverObj.drive()
    }, 300)

    return () => clearTimeout(timer)
  }, [autoStart, isLoading, seen, search, driverObj])

  // Si la página que hospeda el tour se desmonta (ej. el usuario navega desde
  // el menú mientras el tour está activo), destruir el driver para no dejar el
  // overlay huérfano sobre la página nueva. isActive() evita destruir/POSTear
  // de más en el doble-montaje de StrictMode.
  useEffect(() => {
    return () => {
      if (driverObj.isActive()) driverObj.destroy()
    }
  }, [driverObj])

  // startTour estable entre renders para poder registrarlo sin re-disparar
  // efectos (p. ej. useRegisterPageGuide) en cada render del componente host.
  const startTour = useCallback(() => {
    markedRef.current = false
    driverObj.drive()
  }, [driverObj])

  return { startTour }
}
